import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFormConfig, getAllForms } from '../../forms';
import { FormService } from '../../services/FormService';
import { useTenantTrades } from '../../hooks/useTenantTrades';
import { extractFields } from '../../lib/vision/spaceClient';
import { buildFieldSpec } from '../../lib/vision/schemaPrompt';
import { mapExtractedToFormData } from '../../lib/vision/fieldMapper';
import { compressImage, isImage, isPdf } from '../../lib/vision/compressor';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiX,
  FiUpload,
  FiCamera,
  FiFileText,
  FiCpu,
  FiCheckCircle,
  FiAlertTriangle,
  FiRefreshCw,
  FiLoader,
  FiArrowRight,
  FiImage
} = FiIcons;

const SPACE_URL = import.meta.env?.VITE_OCR_SPACE_URL || '';
const ACCESS_TOKEN = import.meta.env?.VITE_OCR_ACCESS_TOKEN || '';
const MAX_FILE_MB = 10;

/**
 * ImportCertificateModal
 * Vision AI certificate import ("clone" a paper/PDF certificate):
 *
 * Step 1 — pick the target form type (trade-gated like the catalog)
 * Step 2 — upload or photograph the certificate (images downscaled on-device)
 * Step 3 — review the AI-mapped fields (AI-written values highlighted) and
 *          create a DRAFT record via FormService.saveForm. The engineer then
 *          verifies everything in the normal editor before any status change.
 *
 * Offline behaviour: extraction requires connectivity to the OCR Space; when
 * offline the modal blocks the extract step with a clear message but never
 * breaks — draft creation itself is fully offline (Dexie + outbox).
 */
export const ImportCertificateModal = ({ isOpen, onClose, defaultFormId }) => {
  const navigate = useNavigate();
  const { allowedForms } = useTenantTrades();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [step, setStep] = useState(1); // 1 select type | 2 capture | 3 review
  const [formId, setFormId] = useState(defaultFormId || '');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [busy, setBusy] = useState(false); // upload + extract in flight
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { formData, appliedPaths, skippedPaths }
  const [saving, setSaving] = useState(false);

  const formOptions = allowedForms || getAllForms();
  const config = useMemo(() => (formId ? getFormConfig(formId) : null), [formId]);

  const reset = useCallback(() => {
    setStep(defaultFormId ? 2 : 1);
    setFormId(defaultFormId || '');
    setFile(null);
    setPreviewUrl('');
    setBusy(false);
    setStatusText('');
    setError('');
    setResult(null);
    setSaving(false);
  }, [defaultFormId]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const appliedSet = useMemo(
    () => new Set(result?.appliedPaths || []),
    [result]
  );

  // Review UI: flatten schema into labeled rows, highlighting AI-filled values.
  const reviewRows = useMemo(() => {
    if (!config || !result) return [];
    const rows = [];
    for (const section of config.schema.sections || []) {
      if (section.repeatable) continue; // handled in editor; keep review simple
      for (const field of section.fields || []) {
        if (!field.id || field.type === 'heading') continue;
        const path = `${section.id}.${field.id}`;
        const value = result.formData?.[section.id]?.[field.id];
        rows.push({
          path,
          label: field.label || field.id,
          sectionTitle: section.title,
          isAi: appliedSet.has(path),
          value: value === null || value === undefined || value === '' ? '—' : String(value),
        });
      }
    }
    return rows;
  }, [config, result, appliedSet]);


  if (!isOpen) return null;

  const handleFileChosen = async (e) => {
    setError('');
    const chosen = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!chosen) return;
    if (!isImage(chosen) && !isPdf(chosen)) {
      setError('Unsupported file type. Use a photo (JPEG/PNG/WebP) or a PDF.');
      return;
    }
    if (chosen.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`File too large (max ${MAX_FILE_MB} MB).`);
      return;
    }
    const prepared = await compressImage(chosen);
    setFile(prepared);
    setPreviewUrl(isImage(prepared) ? URL.createObjectURL(prepared) : '');
    setStep(2);
  };

  const handleExtract = async () => {
    if (!file || !config) return;
    if (!SPACE_URL) {
      setError('Vision import is not configured. Set VITE_OCR_SPACE_URL to your OCR Space URL.');
      return;
    }
    if (!navigator.onLine) {
      setError('You are offline. Extraction needs a connection to the OCR service — try again when back online.');
      return;
    }
    setBusy(true);
    setError('');
    setStatusText('Compressing & uploading…');
    try {
      const fieldSpec = buildFieldSpec(config.schema);
      const response = await extractFields(
        SPACE_URL,
        file,
        formId,
        fieldSpec,
        config.name,
        {
          accessToken: ACCESS_TOKEN,
          onProgress: (p) => setStatusText(
            p.status === 'uploading' ? 'Uploading image…'
              : p.status === 'uploaded' ? 'Analyzing with vision AI…'
              : p.status === 'queued' ? 'Queued for processing…'
              : 'Working…'
          ),
        }
      );
      if (response?.error) {
        setError(`Extraction failed: ${response.detail || response.error}`);
        return;
      }
      const mapped = mapExtractedToFormData(config.defaultData, config.schema, response.fields);
      if (!mapped.appliedPaths.length) {
        setError('The AI could not confidently read any fields. Try a sharper, well-lit photo.');
        return;
      }
      setResult(mapped);
      setStep(3);
    } catch (err) {
      setError(`Extraction failed: ${err.message}. The OCR Space may be cold-starting — try again in a minute.`);
    } finally {
      setBusy(false);
      setStatusText('');
    }
  };

  const handleCreateDraft = async () => {
    if (!config || !result) return;
    setSaving(true);
    setError('');
    try {
      const draft = { ...result.formData, status: 'draft' };
      const saved = await FormService.saveForm(draft, formId, null);
      handleClose();
      if (saved?.id) {
        navigate(`/forms/${formId}/${saved.id}`);
      }
    } catch (err) {
      setError(`Failed to create draft: ${err.message}`);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <SafeIcon icon={FiCpu} className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Import Certificate with Vision AI
              </h2>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Clone an existing paper or PDF certificate into a draft record
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
          {['Select Type', 'Capture', 'Review & Create'].map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <React.Fragment key={label}>
                <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${active ? 'text-blue-600 dark:text-blue-400' : done ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center border ${active ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : done ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950' : 'border-slate-300 dark:border-slate-600'}`}>
                    {done ? <SafeIcon icon={FiCheckCircle} className="w-3 h-3" /> : n}
                  </span>
                  {label}
                </div>
                {i < 2 && <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-xs text-red-700 dark:text-red-300">
              <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: form type */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Which certificate type are you importing? Only your organization's enabled trades are shown.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {formOptions.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => {
                      setFormId(f.id);
                      setStep(2);
                    }}
                    className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${formId === f.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40' : 'border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-slate-900'}`}
                  >
                    <span className="text-xl">{f.icon}</span>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">{f.name}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">{f.category}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: capture */}
          {step === 2 && config && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                <SafeIcon icon={FiFileText} className="w-4 h-4 text-blue-500" />
                Importing as: <span className="text-blue-600 dark:text-blue-400">{config.name}</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-500 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors"
                >
                  <SafeIcon icon={FiUpload} className="w-4 h-4" />
                  Upload photo or PDF
                </button>
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-500 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors"
                >
                  <SafeIcon icon={FiCamera} className="w-4 h-4" />
                  Take photo (mobile)
                </button>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*,.pdf" hidden onChange={handleFileChosen} />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={handleFileChosen} />

              {file && (
                <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/40 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Certificate preview" className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={FiFileText} className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{file.name}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                        {isPdf(file) ? ' • PDF — pages will be combined' : ' • image'}
                      </div>
                    </div>
                    <button
                      onClick={() => { setFile(null); setPreviewUrl(''); }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                      aria-label="Remove file"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed">
                    Tips: fill the frame, avoid glare/shadows, ensure all text is in focus. Printed certificates scan best flat.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: review */}
          {step === 3 && result && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-200">
                  <SafeIcon icon={FiImage} className="w-4 h-4 text-blue-500" />
                  {result.appliedPaths.length} fields extracted
                </div>
                <button
                  onClick={handleExtract}
                  disabled={busy}
                  className="flex items-center gap-1.5 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                >
                  <SafeIcon icon={FiRefreshCw} className="w-3 h-3" />
                  Re-extract
                </button>
              </div>

              <div className="border border-slate-200 dark:border-slate-700 rounded-xl divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-y-auto">
                {reviewRows.map((row) => (
                  <div key={row.path} className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{row.sectionTitle}</div>
                      <div className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{row.label}</div>
                    </div>
                    <div
                      className={`text-xs font-mono px-2 py-1 rounded-md max-w-[50%] truncate ${
                        row.isAi
                          ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}
                      title={row.value}
                    >
                      {row.value}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-[11px] text-amber-800 dark:text-amber-300">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  AI-extracted values are shown in blue and must be verified by a competent engineer
                  before the certificate is completed or issued. Statutory fields (license numbers,
                  safety matrices) are never auto-issued from a photo.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-800">
          <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
            {(busy || saving) && <SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" />}
            {statusText || (busy ? 'Working…' : saving ? 'Creating draft…' : '')}
          </div>
            <div className="flex items-center gap-2">
            {step === 2 && config && file && (
              <button
                onClick={handleExtract}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-bold transition-colors"
              >
                {busy ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiCpu} className="w-4 h-4" />}
                {busy ? 'Extracting…' : 'Extract with AI'}
              </button>
            )}
            {step === 3 && (
              <>
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-600 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Back
                </button>
                <button
                  onClick={handleCreateDraft}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-bold transition-colors"
                >
                  {saving ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />}
                  Create Draft & Open Editor
                </button>
              </>
            )}
            {step === 1 && formOptions.length === 0 && (
              <span className="text-[11px] text-slate-400">No certificate types available for your trades.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportCertificateModal;
